import { useState } from "react";
import { Check, Loader2, MailCheck, Plus, Power, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useAutoResponders,
    useCreateAutoResponder,
    useDeleteAutoResponder,
    useUpdateAutoResponder
} from "@/api/hooks";
import type { AutoResponderRow, UpsertAutoResponderPayload } from "@/api/mailApi";
import { AiTextGenerator } from "./AiTextGenerator";

const EMPTY: UpsertAutoResponderPayload = {
    name: "New auto-responder",
    isEnabled: true,
    sortOrder: 0,
    matchAllMail: true,
    fromSenders: "",
    delaySeconds: 0,
    mode: "template",
    subject: "",
    bodyHtml: "",
    aiInstructions: ""
};

export function AutoRespondersPage() {
    const list = useAutoResponders();
    const create = useCreateAutoResponder();
    const [drafting, setDrafting] = useState(false);

    const rows = list.data ?? [];

    return (
        <div className="mx-auto max-w-3xl">
            <header className="mb-4 flex items-center justify-between gap-4">
                <div>
                    <h2 className="flex items-center gap-2 text-xl font-semibold">
                        <MailCheck className="h-5 w-5 text-primary" /> Auto-responders
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Automatically reply to incoming mail after a delay — to everyone or only
                        specific senders — with a fixed template or an AI reply written from the
                        email’s content. Each sender is answered once per rule.
                    </p>
                </div>
                <Button onClick={() => setDrafting(true)} disabled={drafting}>
                    <Plus className="h-4 w-4" /> New
                </Button>
            </header>

            {drafting ? (
                <Editor
                    initial={{ ...EMPTY, sortOrder: rows.length }}
                    submitLabel="Create"
                    pending={create.isPending}
                    onSubmit={(p) => create.mutate(p, { onSuccess: () => setDrafting(false) })}
                    onCancel={() => setDrafting(false)}
                />
            ) : null}

            {list.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No auto-responders yet.</p>
            ) : (
                <ul className="space-y-2">
                    {rows.map((r) => (
                        <Item key={r.id} row={r} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function Item({ row }: { row: AutoResponderRow }) {
    const update = useUpdateAutoResponder();
    const remove = useDeleteAutoResponder();
    const [editing, setEditing] = useState(false);

    if (editing) {
        return (
            <li>
                <Editor
                    initial={row}
                    submitLabel="Save"
                    pending={update.isPending}
                    onSubmit={(p) =>
                        update.mutate({ id: row.id, payload: p }, { onSuccess: () => setEditing(false) })
                    }
                    onCancel={() => setEditing(false)}
                />
            </li>
        );
    }

    return (
        <li className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="font-medium">{row.name}</h3>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                            row.isEnabled ? "text-emerald-600" : "text-muted-foreground"
                        }`}>
                            {row.isEnabled ? "enabled" : "disabled"}
                        </span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {row.mode === "ai" ? "AI reply" : "template"}
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        <span className="font-semibold">Replies to </span>
                        {row.matchAllMail
                            ? "all incoming mail"
                            : row.fromSenders.trim()
                                ? `senders matching ${row.fromSenders}`
                                : "(no senders configured)"}
                        {" · "}
                        <span className="font-semibold">after </span>
                        {formatDelay(row.delaySeconds)}
                    </p>
                </div>
                <div className="flex gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                            update.mutate({ id: row.id, payload: { ...toPayload(row), isEnabled: !row.isEnabled } })
                        }
                        aria-label={row.isEnabled ? "Disable" : "Enable"}
                        title={row.isEnabled ? "Disable" : "Enable"}
                    >
                        <Power className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)}>Edit</Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={() => {
                            if (window.confirm(`Delete auto-responder "${row.name}"?`)) remove.mutate(row.id);
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </li>
    );
}

function Editor({
    initial,
    submitLabel,
    pending,
    onSubmit,
    onCancel
}: {
    initial: UpsertAutoResponderPayload;
    submitLabel: string;
    pending: boolean;
    onSubmit: (p: UpsertAutoResponderPayload) => void;
    onCancel: () => void;
}) {
    const [form, setForm] = useState<UpsertAutoResponderPayload>({ ...initial });
    const set = <K extends keyof UpsertAutoResponderPayload>(k: K, v: UpsertAutoResponderPayload[K]) =>
        setForm((f) => ({ ...f, [k]: v }));

    const { h, m, s } = splitDelay(form.delaySeconds);
    const setDelay = (hh: number, mm: number, ss: number) =>
        set("delaySeconds", Math.max(0, hh) * 3600 + Math.max(0, mm) * 60 + Math.max(0, ss));

    const submit = () => onSubmit({ ...form, name: form.name.trim() || "Untitled auto-responder" });

    return (
        <div className="mb-4 rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="flex-1" />
                <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={form.isEnabled} onChange={(e) => set("isEnabled", e.target.checked)} />
                    Enabled
                </label>
            </div>

            {/* Match */}
            <div className="mb-3 rounded-md border bg-muted/30 p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reply to</h4>
                <div className="flex flex-col gap-1.5 text-sm">
                    <label className="flex items-center gap-2">
                        <input type="radio" checked={form.matchAllMail} onChange={() => set("matchAllMail", true)} />
                        All incoming mail
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="radio" checked={!form.matchAllMail} onChange={() => set("matchAllMail", false)} />
                        Only specific senders
                    </label>
                    {!form.matchAllMail ? (
                        <Input
                            value={form.fromSenders}
                            onChange={(e) => set("fromSenders", e.target.value)}
                            placeholder="e.g. support@acme.com, @partner.com"
                            className="mt-1 h-8"
                        />
                    ) : null}
                    {!form.matchAllMail ? (
                        <p className="text-[10px] text-muted-foreground">
                            Comma-separated. Substring match — <code>@partner.com</code> matches everyone at that domain.
                        </p>
                    ) : null}
                </div>
            </div>

            {/* Delay */}
            <div className="mb-3 rounded-md border bg-muted/30 p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Delay before replying</h4>
                <div className="flex items-center gap-2 text-sm">
                    <DelayField label="hours" value={h} onChange={(v) => setDelay(v, m, s)} />
                    <DelayField label="min" value={m} onChange={(v) => setDelay(h, v, s)} />
                    <DelayField label="sec" value={s} onChange={(v) => setDelay(h, m, v)} />
                    <span className="text-xs text-muted-foreground">
                        {form.delaySeconds === 0 ? "sends immediately" : `= ${formatDelay(form.delaySeconds)}`}
                    </span>
                </div>
            </div>

            {/* Response */}
            <div className="mb-3 rounded-md border bg-muted/30 p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Response</h4>
                <div className="mb-2 flex gap-4 text-sm">
                    <label className="flex items-center gap-2">
                        <input type="radio" checked={form.mode === "template"} onChange={() => set("mode", "template")} />
                        Fixed template
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="radio" checked={form.mode === "ai"} onChange={() => set("mode", "ai")} />
                        AI reply from the email
                    </label>
                </div>

                {form.mode === "template" ? (
                    <div className="space-y-2">
                        <Input
                            value={form.subject}
                            onChange={(e) => set("subject", e.target.value)}
                            placeholder="Subject (blank = Re: original subject)"
                            className="h-8"
                        />
                        <div className="flex items-center justify-between gap-2">
                            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Reply body
                            </label>
                            <AiTextGenerator
                                mode="autoreply"
                                title="Draft the reply with AI"
                                firstPlaceholder='Describe it — e.g. "thank them, say we reply within one business day"'
                                buildInitial={(input) => input.trim() || "Write a short, friendly auto-acknowledgement."}
                                onApply={(html) => set("bodyHtml", html)}
                            />
                        </div>
                        <textarea
                            value={form.bodyHtml}
                            onChange={(e) => set("bodyHtml", e.target.value)}
                            rows={5}
                            placeholder="Thanks for your email — we’ve received it and will reply shortly."
                            className="w-full rounded-md border p-2 text-sm"
                        />
                    </div>
                ) : (
                    <div className="space-y-1">
                        <textarea
                            value={form.aiInstructions}
                            onChange={(e) => set("aiInstructions", e.target.value)}
                            rows={4}
                            placeholder='Instructions for the AI — e.g. "acknowledge their request, answer any simple question, and say a colleague will follow up within 24 hours."'
                            className="w-full rounded-md border p-2 text-sm"
                        />
                        <p className="text-[10px] text-muted-foreground">
                            The AI reads each incoming email and writes a tailored reply signed as you.
                            It never invents facts, prices or commitments.
                        </p>
                    </div>
                )}
            </div>

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button onClick={submit} disabled={pending}>
                    {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    {submitLabel}
                </Button>
            </div>
        </div>
    );
}

function DelayField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
    return (
        <label className="flex items-center gap-1">
            <input
                type="number"
                min={0}
                value={value}
                onChange={(e) => onChange(Number(e.target.value) || 0)}
                className="h-8 w-16 rounded-md border px-2 text-sm"
                aria-label={label}
            />
            <span className="text-xs text-muted-foreground">{label}</span>
        </label>
    );
}

function splitDelay(total: number) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return { h, m, s };
}

function formatDelay(total: number): string {
    if (total <= 0) return "immediately";
    const { h, m, s } = splitDelay(total);
    return [h && `${h}h`, m && `${m}m`, s && `${s}s`].filter(Boolean).join(" ");
}

function toPayload(r: AutoResponderRow): UpsertAutoResponderPayload {
    const { id: _id, ...rest } = r;
    return rest;
}
